import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DataService} from '../../services/data.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BehaviorSubject, Subject} from 'rxjs';
import moment from 'moment-timezone';
import * as XLSX from 'xlsx';
import {takeUntil} from 'rxjs/operators';
import {BudgetListTableService} from '../../services/budget-list-table.service';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {
    MatCell,
    MatCellDef, MatColumnDef,
    MatHeaderCell, MatHeaderCellDef,
    MatHeaderRow, MatHeaderRowDef,
    MatRow, MatRowDef,
    MatTable,
    MatTableDataSource
} from '@angular/material/table';
import {MatPaginator} from '@angular/material/paginator';
import {MatInput} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatButtonModule} from '@angular/material/button';
import {BudgetListFilterComponent} from '../../shared/budget-list-filter/budget-list-filter.component';
import {MatCard} from '@angular/material/card';
import {TimestampPipe} from '../../shared/pipes/timestamp.pipe';
import {DatePipe} from '@angular/common';
import {MatMomentDateModule} from '@angular/material-moment-adapter';



@Component({
    selector: 'app-reporting',
    templateUrl: './reporting.component.html',
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatTabGroup,
        MatTab,
        MatDatepickerModule,
        MatButtonModule,
        BudgetListFilterComponent,
        MatCard,
        MatTable,
        MatHeaderCell,
        TimestampPipe,
        DatePipe,
        MatCell,
        MatHeaderRow,
        MatRow,
        MatPaginator,
        MatCellDef,
        MatSort,
        MatSortHeader,
        MatHeaderRowDef,
        MatCellDef,
        MatCellDef,
        MatColumnDef,
        MatInput,
        MatCellDef,
        MatColumnDef,
        MatCellDef,
        MatColumnDef,
        MatColumnDef,
        MatCellDef,
        MatInput,
        MatCellDef,
        MatColumnDef,
        MatRowDef,
        MatColumnDef,
        MatColumnDef,
        MatInput,
        MatInput,
        MatHeaderCellDef,
        MatHeaderCellDef,
        MatMomentDateModule
    ],
    styleUrls: ['./reporting.component.scss']
})
export class ReportingComponent implements OnInit, OnDestroy, AfterViewInit {

    private unsubscribe$ = new Subject();
    loading = false;
    reportFiles = new MatTableDataSource<any>();
    reportsColumns = ['createdAt', 'endAt', 'status', 'fileReference', 'type', 'period', 'details'];

    REPORT_GENERATING_MSG = `Report generation started. Results will be ready in few minutes..`;

    @ViewChild('oreCommesseFromDate', {
        read: MatInput
    }) oreCommesseFromDate;
    @ViewChild('oreCommesseToDate', {
        read: MatInput
    }) oreCommesseToDate;
    public oreCommesseFromDateSubject = new BehaviorSubject<Date>(moment().subtract(1, 'year').toDate());
    public oreCommesseToDateSubject = new BehaviorSubject<Date>(moment().toDate());

    @ViewChild('reportFromDate', {
        read: MatInput
    }) reportFromDate;

    @ViewChild('reportToDate', {
        read: MatInput
    }) reportToDate;
    public reportDateFromSubject = new BehaviorSubject<Date>(moment().subtract(1, 'year').toDate());
    public reportDateToSubject = new BehaviorSubject<Date>(moment().toDate());

    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    constructor(private dataService: DataService,
                private budgetListTableService: BudgetListTableService,
                private _snackbar: MatSnackBar) {
    }

    ngOnInit(): void {
        this.dataService.listReportingFiles()
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(result => {
                this.reportFiles.data = result;
            });
    }

    ngAfterViewInit() {
        this.reportFiles.sort = this.sort;
        this.reportFiles.paginator = this.paginator;
    }

    openOreCommesseLink(id: string) {
        this.loading = true;

        this.dataService.getReportDownloadLink(id)
            .subscribe(result => {
                this.loading = false;
                if (result && result.url) {
                    window.location.href = result.url + '&response-content-disposition=attachment;';
                }
            }, error => {
                this.loading = false;
                console.log(error);
                this._snackbar.open(`Something went wrong..`,
                    'close',
                    {duration: 6000});
            });
    }

    generateOreCommesseReport() {
        const from = moment(this.oreCommesseFromDate.value).format('YYYY-MM-DD');
        const to = moment(this.oreCommesseToDate.value).format('YYYY-MM-DD');

        this.loading = true;
        console.log(`generateOreCommesseReport from ${from} to ${to}`);
        this.dataService.generateReportOreCommesse(from, to)
            .subscribe(() => {
                this.loading = false;
                this._snackbar.open(this.REPORT_GENERATING_MSG,
                    'close',
                    {duration: 6000});
            }, error => {
                this.loading = false;
                console.log(error);
                this._snackbar.open(`Something went wrong starting the report generation.`,
                    'close',
                    {duration: 6000});
            });
    }

    async generateFilteredReport() {

        try {
            this.loading = true;
            await this.budgetListTableService.getHoursReportByBudgetListFilter();
            this._snackbar.open(this.REPORT_GENERATING_MSG,
                'close',
                {duration: 6000});
            this.loading = false;
        } catch (err) {
            console.error(err);
            this.loading = false;
        }
    }

    async generateFilteredSummaryReport() {

        try {
            this.loading = true;
            await this.budgetListTableService.getSummaryReportByBudgetListFilter();
            this._snackbar.open(this.REPORT_GENERATING_MSG,
                'close',
                {duration: 6000});
            this.loading = false;
        } catch (err) {
            console.error(err);
            this.loading = false;
        }
    }

    async downloadHourReport() {

        const from = this.reportFromDate.value.toDate();
        const to = this.reportToDate.value.toDate();
        console.log('Creating excel with dates: ', from, to);

        try {
            this.loading = true;
            const reportData = await this.dataService.getHoursReport(from, to).toPromise();
            console.log('Got data: ', reportData);
            XLSX.writeFile(reportData.wb, reportData.fileName);

            this.loading = false;
        } catch (err) {
            console.error(err);
            this.loading = false;
        }

    }


    public ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

}
